import React, { useCallback } from 'react';
import { Button, InputNumber, Input, Select, Table, Space, Typography, Row, Col, Divider } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FH111, FH081 } from '../../../services/pwh205Api';
import { ITEM_TYPE_OPTIONS, fmt } from '../helpers';
import { AcceptanceFieldHint, pwh205f1AcceptanceManifest } from '../../../features/acceptance';

interface DetailGridProps {
  details: Partial<FH111>[];
  isEditing: boolean;
  onChange: (details: Partial<FH111>[]) => void;
  expenseNames: Record<string, string>;
  onExpenseBlur: (code: string, idx: number) => void;
  detailCount: number;
  paidTotal: number;
  writeOffRecords: FH081[];
  documentKey?: string;
}

const DetailGrid: React.FC<DetailGridProps> = ({
  details, isEditing, onChange, expenseNames, onExpenseBlur,
  detailCount, paidTotal, writeOffRecords, documentKey,
}) => {
  const H = (fieldId: string) => (
    <AcceptanceFieldHint
      manifest={pwh205f1AcceptanceManifest}
      fieldId={fieldId}
      documentKey={documentKey}
    />
  );
  // BR-F21: Add detail row with default values
  const handleAdd = useCallback(() => {
    const newRow: Partial<FH111> = {
      fh111_02: 1,
      fh111_03: details.length + 1,
      fh111_12: '05', // default: 暫借款
      fh111_15: 0,
      fh111_19: '',
      fh111_37: '',
      fh111_90: 0,
    };
    onChange([...details, newRow]);
  }, [details, onChange]);

  // BR-F22: Delete current detail row and reindex
  const handleDelete = useCallback((idx: number) => {
    const updated = details
      .filter((_, i) => i !== idx)
      .map((d, i) => ({ ...d, fh111_03: i + 1 })); // T-F05: reindex 1-based
    onChange(updated);
  }, [details, onChange]);

  const handleChange = useCallback((idx: number, field: keyof FH111, value: unknown) => {
    const updated = [...details];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange(updated);
  }, [details, onChange]);

  const columns: ColumnsType<Partial<FH111>> = [
    {
      title: <>項次{H('fh111_03')}</>,
      dataIndex: 'fh111_03',
      width: 55,
      align: 'center',
    },
    {
      title: <>費用代號{H('fh111_19')}</>,
      dataIndex: 'fh111_19',
      width: 130,
      render: (val, _, idx) => {
        if (!isEditing) return val;
        return (
          <Input
            value={val}
            maxLength={12}
            onChange={e => handleChange(idx, 'fh111_19', e.target.value)}
            onBlur={() => onExpenseBlur(details[idx]?.fh111_19 || '', idx)}
          />
        );
      },
    },
    {
      title: <>費用名稱{H('f109_11')}</>,
      width: 130,
      render: (_, row) => {
        const code = row.fh111_19 || '';
        return expenseNames[code] || '';
      },
    },
    {
      title: <>款項別{H('fh111_12')}</>,
      dataIndex: 'fh111_12',
      width: 130,
      render: (val, _, idx) => {
        if (!isEditing) {
          const opt = ITEM_TYPE_OPTIONS.find(o => o.value === val);
          return opt ? opt.label : val;
        }
        return (
          <Select
            value={val}
            style={{ width: '100%' }}
            options={ITEM_TYPE_OPTIONS}
            onChange={v => handleChange(idx, 'fh111_12', v)}
          />
        );
      },
    },
    {
      title: <>請款金額{H('fh111_15')}</>,
      dataIndex: 'fh111_15',
      width: 120,
      align: 'right',
      render: (val, _, idx) => {
        if (!isEditing) return fmt(val as number);
        return (
          <InputNumber
            value={val}
            precision={2}
            style={{ width: '100%' }}
            onChange={v => handleChange(idx, 'fh111_15', v ?? 0)}
          />
        );
      },
    },
    ...(isEditing ? [{
      title: '操作',
      width: 60,
      render: (_: unknown, __: unknown, idx: number) => (
        <Button size="small" danger onClick={() => handleDelete(idx)}>刪</Button>
      ),
    }] : []),
  ];

  // Write-off records (FH081) read-only grid columns
  const writeOffColumns: ColumnsType<FH081> = [
    { title: <>沖帳單號{H('fh081_03')}</>, dataIndex: 'fh081_03', width: 120 },
    { title: <>金額{H('fh081_11')}</>, dataIndex: 'fh081_11', width: 140, align: 'right',
      render: (val: number) => fmt(val) },
    { title: <>日期{H('fh081_12')}</>, dataIndex: 'fh081_12', width: 120,
      render: (val: string | null) => val ? val.substring(0, 10) : '' },
  ];

  return (
    <div className="pwh205-detail-grid">
      {/* Summary row: fh110_39 (總項次) + fh110_21 (已付款總額) */}
      <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
        <Col span={4}>
          <div className="field-row">
            <label>總項次{H('fh110_39')}</label>
            <Input value={detailCount} readOnly disabled style={{ textAlign: 'right' }} />
          </div>
        </Col>
        <Col span={4}>
          <div className="field-row">
            <label>已付款總額{H('fh110_21')}</label>
            <Input value={fmt(paidTotal)} readOnly disabled style={{ textAlign: 'right' }} />
          </div>
        </Col>
      </Row>

      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button type="dashed" onClick={handleAdd} disabled={details.length >= 99}>
            + 明細新增
          </Button>
        </Space>
      )}
      <Table
        rowKey={(_, idx) => String(idx)}
        dataSource={details}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: 800 }}
        bordered
      />

      {/* Grid2: 沖帳明細 (FH081) — read-only reference */}
      {writeOffRecords.length > 0 && (
        <>
          <Divider />
          <Typography.Text strong style={{ marginBottom: 8, display: 'block' }}>沖帳明細</Typography.Text>
          <Table
            rowKey={(r) => r.fh081_01}
            dataSource={writeOffRecords}
            columns={writeOffColumns}
            pagination={false}
            size="small"
            scroll={{ x: 400 }}
            bordered
          />
        </>
      )}
    </div>
  );
};

export default DetailGrid;
